'use client';

import { useEffect, useRef } from 'react';
import { alpha } from '@mui/material/styles';
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
} from '@/primitives';
import { black } from '@/lib/colours';
import { useResolvedColorScheme } from '@/hooks/useResolvedColorScheme';
import type { MatingSearchFormValues } from './types';
import { MATING_DISPLAY_BY } from './types';
import {
  quickSearchInputSx,
  quickSearchOutlinedBorder,
} from '../search/constants';
import CalvingYearChipInput from '../search/components/CalvingYearChipInput';
import AnimalIdentMultiSelect from './AnimalIdentMultiSelect';
import { WildcardKind } from '../search/identChipWildcardUi';

const matingBarSelectSx = {
  minWidth: 118,
  height: { xs: 32, md: 36 },
  borderRadius: '8px',
  backgroundColor: 'background.paper',
  fontSize: '13px',
  color: 'text.primary',
  '& .MuiSelect-select': {
    py: 0,
    display: 'flex',
    alignItems: 'center',
  },
} as const;

const matingAutocompleteBarSx = {
  '& .MuiInputLabel-root': {
    fontSize: '13px',
    color: 'text.secondary',
  },
  '& .MuiOutlinedInput-root.MuiAutocomplete-inputRoot': {
    alignItems: 'center',
    color: 'text.primary',
  },
  '& input::placeholder': {
    color: 'text.secondary',
    opacity: 1,
    fontSize: '13px',
  },
} as const;

type MatingPredictorSearchProps = {
  formValues: MatingSearchFormValues;
  onFormChange: (values: MatingSearchFormValues) => void;
  onSearch: () => void;
  onClear: () => void;
  loading: boolean;
};

const defaultFormValues: MatingSearchFormValues = {
  sireIdents: '',
  damIdents: '',
  damCalvingYears: '',
  maxAnimals: 50,
  displayBy: MATING_DISPLAY_BY.SIRE,
};

export { defaultFormValues };

export default function MatingPredictorSearch({
  formValues,
  onFormChange,
  onSearch,
  onClear,
  loading,
}: MatingPredictorSearchProps) {
  const { isDarkMode } = useResolvedColorScheme();

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && canSearch) {
      onSearch();
    }
  };

  const canSearch =
    formValues.sireIdents.trim() !== '' && formValues.damIdents.trim() !== '';

  const sireInputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    sireInputRef.current?.focus();
  }, []);

  return (
    <Box
      sx={{
        flexShrink: 0,
        mb: 1.5,
        pt: { xs: '12px', md: '8px' },
        pb: { xs: '8px', md: '8px' },
        px: { xs: '8px', md: '10px' },
        backgroundColor: 'background.paper',
        borderRadius: '8px',
        boxShadow: `0 2px 8px ${alpha(black[900], isDarkMode ? 0.4 : 0.08)}`,
      }}
      onKeyDown={handleKeyDown}
    >
      <Stack
        direction="row"
        sx={{
          flexWrap: 'wrap',
          alignItems: 'flex-end',
          gap: { xs: '8px', md: '10px' },
        }}
      >
        <AnimalIdentMultiSelect
          label="Sire Identifier(s)"
          size="small"
          ariaRequired
          value={formValues.sireIdents}
          onChange={(val) => onFormChange({ ...formValues, sireIdents: val })}
          inputRef={sireInputRef}
          sexFilter="M"
          showExplicitWildcardOptions
          plainTextBlurWildcard={WildcardKind.contains}
          sx={[
            matingAutocompleteBarSx,
            { flex: { xs: '1 1 100%', md: '1 1 200px' } },
          ]}
        />
        <AnimalIdentMultiSelect
          label="Dam Identifier(s)"
          size="small"
          ariaRequired
          value={formValues.damIdents}
          onChange={(val) => onFormChange({ ...formValues, damIdents: val })}
          sexFilter="F"
          showExplicitWildcardOptions
          plainTextBlurWildcard={WildcardKind.contains}
          sx={[
            matingAutocompleteBarSx,
            { flex: { xs: '1 1 100%', md: '1 1 200px' } },
          ]}
        />
        <Stack
          direction="row"
          useFlexGap
          sx={{
            alignItems: 'flex-end',
            gap: { xs: 1, md: '10px' },
            flex: { xs: '1 1 100%', md: '1 1 320px' },
            minWidth: 0,
            width: { xs: '100%', md: 'auto' },
          }}
        >
          <Box
            sx={{
              flex: '1 1 0',
              minWidth: 0,
              maxWidth: { md: 280 },
            }}
          >
            <CalvingYearChipInput
              label="Calving Year(s)"
              value={formValues.damCalvingYears}
              onChange={(val) =>
                onFormChange({ ...formValues, damCalvingYears: val })
              }
              sx={[
                quickSearchInputSx,
                matingAutocompleteBarSx,
                {
                  '& .MuiInputBase-root': {
                    height: 'auto',
                    minHeight: { xs: 32, md: 36 },
                  },
                  '& .MuiOutlinedInput-root.MuiAutocomplete-inputRoot': {
                    minHeight: { xs: 32, md: 36 },
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    paddingTop: '2px',
                    paddingBottom: '2px',
                    paddingLeft: '6px',
                    paddingRight: '32px !important',
                    fontSize: '16px', // iOS Safari auto-zooms inputs with font-size < 16px and doesn't zoom back out
                  },
                  '& .MuiAutocomplete-input': {
                    py: '4px',
                    minHeight: 0,
                  },
                  '& .MuiChip-root': {
                    height: 22,
                    '& .MuiChip-label': { px: 0.75, fontSize: '12px' },
                  },
                },
              ]}
            />
          </Box>
          <FormControl
            size="small"
            sx={{
              flexShrink: 0,
              flex: { xs: '0 0 118px', md: '0 0 auto' },
              minWidth: 118,
              '& .MuiInputLabel-root': {
                fontSize: '13px',
                color: 'text.secondary',
              },
            }}
          >
            <InputLabel id="mating-max-dams-label">Max Dams</InputLabel>
            <Select
              labelId="mating-max-dams-label"
              label="Max Dams"
              value={formValues.maxAnimals}
              onChange={(e) =>
                onFormChange({
                  ...formValues,
                  maxAnimals: Number(e.target.value),
                })
              }
              sx={matingBarSelectSx}
              slotProps={{
                notchedOutline: {
                  sx: { border: quickSearchOutlinedBorder },
                },
              }}
              MenuProps={{
                slotProps: { paper: { sx: { maxHeight: 320 } } },
              }}
            >
              {[50, 100, 150, 200].map((val) => (
                <MenuItem key={val} value={val} sx={{ fontSize: '13px' }}>
                  {val}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
        <Stack
          direction="row"
          spacing={1}
          sx={{
            flex: { xs: '1 1 100%', md: '0 0 auto' },
            justifyContent: { xs: 'flex-end', md: 'flex-start' },
            width: { xs: '100%', md: 'auto' },
          }}
        >
          <Button
            variant="outlined"
            size="small"
            onClick={onClear}
            disabled={loading}
            sx={{
              height: { xs: 32, md: 36 },
              minWidth: 72,
              borderRadius: '8px',
              whiteSpace: 'nowrap',
              fontSize: '13px',
              textTransform: 'none',
              color: 'text.primary',
              borderColor: 'divider',
              '&:hover': {
                borderColor: 'text.secondary',
                backgroundColor: 'action.hover',
              },
            }}
          >
            Clear
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={onSearch}
            disabled={!canSearch || loading}
            sx={{
              height: { xs: 32, md: 36 },
              minWidth: 88,
              borderRadius: '8px',
              whiteSpace: 'nowrap',
              fontSize: '13px',
              textTransform: 'none',
              boxShadow: 'none',
              '&:hover': { boxShadow: 'none' },
            }}
          >
            {loading ? 'Searching...' : 'Search'}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
}
